import { Button, InputGroup, Text, TextField } from "@/components/ui"
import { useId, useState, type FormEvent, type KeyboardEvent } from "react"

/** What a submitted code turned into: accepted, or the short line to show under the field. */
export type CouponCodeSubmit = { ok: true } | { ok: false; error: string }

type CouponCodeFormProps = {
  /** The code this one takes the place of ("Trocar"); absent when adding. */
  replacing?: string
  onSubmitCode: (raw: string) => CouponCodeSubmit
  onDone: () => void
  onCancel: () => void
}

function focusOnMount(node: HTMLInputElement | null) {
  node?.focus({ preventScroll: true })
}

/** The coupon field shared by the bag and the checkout: a code, "Aplicar" in the store colour, and the error the customer just caused, short and inline. */
export function CouponCodeForm({ replacing, onSubmitCode, onDone, onCancel }: CouponCodeFormProps) {
  const [input, setInput] = useState("")
  const [error, setError] = useState<string | null>(null)
  const inputId = useId()
  const errorId = useId()

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const result = onSubmitCode(input)
    if (!result.ok) {
      setError(result.error)
      return
    }
    onDone()
  }

  function cancelOnEscape(event: KeyboardEvent<HTMLFormElement>) {
    if (event.key !== "Escape") return
    event.preventDefault()
    onCancel()
  }

  return (
    <form onSubmit={submit} onKeyDown={cancelOnEscape} noValidate>
      <label htmlFor={inputId} className="sr-only">
        {replacing ? `Novo código no lugar de ${replacing}` : "Código do cupom"}
      </label>
      <InputGroup>
        <TextField
          ref={focusOnMount}
          id={inputId}
          placeholder="Digite seu cupom"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          value={input}
          invalid={error !== null}
          aria-invalid={error !== null}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => {
            setInput(event.target.value.toUpperCase())
            setError(null)
          }}
        />
        <Button type="submit" tone="primary">
          Aplicar
        </Button>
      </InputGroup>
      <div id={errorId} role="status" aria-live="polite">
        {error ? (
          <Text size="sm" tone="danger" className="mt-1.5">
            {error}
          </Text>
        ) : null}
      </div>
      <Button type="button" variant="link" tone="neutral" size="auto" weight="normal" onClick={onCancel} className="mt-2">
        <Text as="span" size="sm">
          Cancelar
        </Text>
      </Button>
    </form>
  )
}
